import { useCallback, useEffect, useState } from 'react';
import {
  crossFundTransfer,
  getAutoBalanceStatus,
  getFundPortfolio,
  internalFundTransfer,
  listFundTransfers,
  runAutoBalanceOnce,
  updateAutoBalanceConfig,
  type AutoBalanceConfigView,
  type FundPortfolioView,
  type TransferRecordView,
} from '@/services/manualTradingApi';

const FUND_EXCHANGES = ['OKX', 'Bybit'] as const;
type FundExchange = (typeof FUND_EXCHANGES)[number];

const TRANSFER_STATUS_LABEL: Record<string, string> = {
  pending: '待处理',
  transferring_internal: '所内划转中',
  withdrawing: '提币中',
  waiting_arrival: '等待到账',
  deposit_to_trading: '归位交易中',
  completed: '已完成',
  failed: '失败',
  cancelled: '已取消',
};

function formatUsd(v: number | null | undefined, digits = 2): string {
  if (v == null || Number.isNaN(v)) return '—';
  return v.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: digits,
  });
}

function formatPct(v: number | null | undefined): string {
  if (v == null || Number.isNaN(v)) return '—';
  return `${(v * 100).toFixed(1)}%`;
}

function formatTs(iso: string | null | undefined): string {
  if (!iso) return '—';
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return iso;
  return new Date(ms).toLocaleString('zh-CN', { hour12: false });
}

function statusLabel(status: string): string {
  return TRANSFER_STATUS_LABEL[status] ?? status;
}

function statusClass(status: string): string {
  if (status === 'completed') return 'success';
  if (status === 'failed' || status === 'cancelled') return 'failed';
  return 'pending';
}

type FundTransferSectionProps = {
  onError?: (message: string | null) => void;
};

export function FundTransferSection({ onError }: FundTransferSectionProps) {
  const [portfolio, setPortfolio] = useState<FundPortfolioView | null>(null);
  const [autoConfig, setAutoConfig] = useState<AutoBalanceConfigView | null>(null);
  const [hasInFlight, setHasInFlight] = useState(false);
  const [transfers, setTransfers] = useState<TransferRecordView[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const [internalExchange, setInternalExchange] = useState<FundExchange>('OKX');
  const [internalAmount, setInternalAmount] = useState('');
  const [internalToFunding, setInternalToFunding] = useState(true);

  const [crossDirection, setCrossDirection] = useState<'OKX_BYBIT' | 'BYBIT_OKX'>('OKX_BYBIT');
  const [crossAmount, setCrossAmount] = useState('');

  const loadFundData = useCallback(async () => {
    setLoading(true);
    try {
      const [portfolioRow, statusRow, transferRows] = await Promise.all([
        getFundPortfolio(),
        getAutoBalanceStatus(),
        listFundTransfers(20),
      ]);
      setPortfolio(portfolioRow);
      setAutoConfig(statusRow.config);
      setHasInFlight(statusRow.has_in_flight_transfer);
      if (statusRow.portfolio) setPortfolio(statusRow.portfolio);
      setTransfers(transferRows);
      onError?.(null);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      onError?.(msg);
    } finally {
      setLoading(false);
    }
  }, [onError]);

  useEffect(() => {
    void loadFundData();
  }, [loadFundData]);

  const refreshAfterAction = async (info: string) => {
    setMessage(info);
    await loadFundData();
  };

  const onInternalTransfer = async () => {
    const amount = internalAmount.trim();
    if (!amount || Number(amount) <= 0) {
      setMessage('请输入有效划转金额');
      return;
    }
    setSubmitting(true);
    setMessage(null);
    try {
      const res = await internalFundTransfer({
        exchange: internalExchange,
        currency: 'USDT',
        amount,
        to_funding: internalToFunding,
      });
      setInternalAmount('');
      await refreshAfterAction(
        `所内划转已提交（${internalExchange} ${internalToFunding ? '交易→资金' : '资金→交易'}），计划 ID：${res.plan_id}`,
      );
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setMessage(msg);
      onError?.(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const onCrossTransfer = async () => {
    const amount = Number(crossAmount.trim());
    if (!Number.isFinite(amount) || amount <= 0) {
      setMessage('请输入有效跨所划转金额');
      return;
    }
    const [source, target] =
      crossDirection === 'OKX_BYBIT' ? (['OKX', 'Bybit'] as const) : (['Bybit', 'OKX'] as const);
    setSubmitting(true);
    setMessage(null);
    try {
      const res = await crossFundTransfer({
        source_exchange: source,
        target_exchange: target,
        currency: 'USDT',
        amount,
      });
      setCrossAmount('');
      await refreshAfterAction(
        `跨所划转已提交（${source} → ${target}），计划 ID：${res.plan_id}。链上到账可能需要数分钟，请稍后刷新记录。`,
      );
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setMessage(msg);
      onError?.(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const onToggleAutoBalance = async () => {
    if (!autoConfig) return;
    setSubmitting(true);
    setMessage(null);
    try {
      const next = await updateAutoBalanceConfig({
        ...autoConfig,
        enabled: !autoConfig.enabled,
      });
      setAutoConfig(next);
      await refreshAfterAction(next.enabled ? '已开启自动平衡' : '已关闭自动平衡');
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setMessage(msg);
      onError?.(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const onRunAutoBalanceOnce = async () => {
    setSubmitting(true);
    setMessage(null);
    try {
      const res = await runAutoBalanceOnce();
      if (res) {
        await refreshAfterAction(`自动平衡已触发，计划 ID：${res.plan_id}`);
      } else {
        await refreshAfterAction('当前无需平衡（未超阈值或处于冷却期）');
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setMessage(msg);
      onError?.(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const renderExchangeBalance = (row: FundPortfolioView['okx']) => (
    <div key={row.exchange} className="fund-transfer-exchange-card">
      <div className="fund-transfer-exchange-head">
        <span className="fund-transfer-exchange-name">{row.exchange}</span>
        <span className="fund-transfer-exchange-total">{formatUsd(row.balance_usd)}</span>
      </div>
      <div className="fund-transfer-exchange-rows">
        <span>交易 USDT {row.usdt_trading.toLocaleString('en-US', { maximumFractionDigits: 2 })}</span>
        <span>资金 USDT {row.usdt_funding.toLocaleString('en-US', { maximumFractionDigits: 2 })}</span>
      </div>
    </div>
  );

  return (
    <div className="account-overview-section fund-transfer-section">
      <div className="fund-transfer-head">
        <div>
          <h3>资金划转</h3>
          <p className="fund-transfer-desc">
            OKX / Bybit 所内划转与跨所平衡。跨所提币走白名单链上路由，耗时较长；所有操作均记录在后端。
          </p>
        </div>
        <button type="button" className="secondary" onClick={() => void loadFundData()} disabled={loading || submitting}>
          {loading ? '刷新中…' : '刷新余额'}
        </button>
      </div>

      {hasInFlight ? (
        <p className="fund-transfer-banner">有进行中的划转任务，请等待完成后再发起新操作。</p>
      ) : null}

      {message ? <p className="fund-transfer-message">{message}</p> : null}

      {portfolio ? (
        <div className="fund-transfer-summary">
          <div className="fund-transfer-summary-stat">
            <span className="label">双所总权益</span>
            <span className="value">{formatUsd(portfolio.total_usd)}</span>
          </div>
          <div className="fund-transfer-summary-stat">
            <span className="label">差额比例</span>
            <span className={`value${portfolio.diff_ratio > (autoConfig?.threshold_ratio ?? 0.3) ? ' warn' : ''}`}>
              {formatPct(portfolio.diff_ratio)}
            </span>
          </div>
          <div className="fund-transfer-exchange-grid">
            {renderExchangeBalance(portfolio.okx)}
            {renderExchangeBalance(portfolio.bybit)}
          </div>
        </div>
      ) : (
        <p className="account-overview-empty">
          {loading ? '加载余额…' : '暂无 OKX/Bybit 余额，请先在「账户与 API」配置密钥'}
        </p>
      )}

      <div className="fund-transfer-panels">
        <div className="fund-transfer-panel">
          <h4>所内划转</h4>
          <div className="fund-transfer-form">
            <label>
              交易所
              <select
                value={internalExchange}
                onChange={(e) => setInternalExchange(e.target.value as FundExchange)}
                disabled={submitting || hasInFlight}
              >
                {FUND_EXCHANGES.map((ex) => (
                  <option key={ex} value={ex}>
                    {ex}
                  </option>
                ))}
              </select>
            </label>
            <label>
              方向
              <select
                value={internalToFunding ? 'to_funding' : 'to_trading'}
                onChange={(e) => setInternalToFunding(e.target.value === 'to_funding')}
                disabled={submitting || hasInFlight}
              >
                <option value="to_funding">交易账户 → 资金账户</option>
                <option value="to_trading">资金账户 → 交易账户</option>
              </select>
            </label>
            <label>
              金额 (USDT)
              <input
                type="number"
                min="0"
                step="any"
                placeholder="100"
                value={internalAmount}
                onChange={(e) => setInternalAmount(e.target.value)}
                disabled={submitting || hasInFlight}
              />
            </label>
            <button
              type="button"
              onClick={() => void onInternalTransfer()}
              disabled={submitting || hasInFlight || loading}
            >
              {submitting ? '提交中…' : '提交所内划转'}
            </button>
          </div>
        </div>

        <div className="fund-transfer-panel">
          <h4>跨所划转</h4>
          <div className="fund-transfer-form">
            <label>
              方向
              <select
                value={crossDirection}
                onChange={(e) => setCrossDirection(e.target.value as 'OKX_BYBIT' | 'BYBIT_OKX')}
                disabled={submitting || hasInFlight}
              >
                <option value="OKX_BYBIT">OKX → Bybit</option>
                <option value="BYBIT_OKX">Bybit → OKX</option>
              </select>
            </label>
            <label>
              金额 (USDT)
              <input
                type="number"
                min="0"
                step="any"
                placeholder="500"
                value={crossAmount}
                onChange={(e) => setCrossAmount(e.target.value)}
                disabled={submitting || hasInFlight}
              />
            </label>
            <button
              type="button"
              onClick={() => void onCrossTransfer()}
              disabled={submitting || hasInFlight || loading}
            >
              {submitting ? '提交中…' : '提交跨所划转'}
            </button>
            <p className="fund-transfer-hint">跨所流程含链上提币与到账轮询，请求可能持续数分钟。</p>
          </div>
        </div>

        <div className="fund-transfer-panel">
          <h4>自动平衡</h4>
          <div className="fund-transfer-auto">
            <div className="fund-transfer-auto-row">
              <span>
                状态：
                <strong>{autoConfig?.enabled ? '已开启' : '已关闭'}</strong>
              </span>
              <span>阈值 {formatPct(autoConfig?.threshold_ratio ?? 0.3)}</span>
            </div>
            <div className="fund-transfer-auto-actions">
              <button
                type="button"
                className={autoConfig?.enabled ? 'secondary' : undefined}
                onClick={() => void onToggleAutoBalance()}
                disabled={submitting || !autoConfig}
              >
                {autoConfig?.enabled ? '关闭自动平衡' : '开启自动平衡'}
              </button>
              <button
                type="button"
                className="secondary"
                onClick={() => void onRunAutoBalanceOnce()}
                disabled={submitting || hasInFlight || loading}
              >
                立即执行一次
              </button>
            </div>
            <p className="fund-transfer-hint">
              当 OKX 与 Bybit 差额比例超过阈值时自动跨所平衡；冷却 {autoConfig?.cooldown_secs ?? 600}s。
            </p>
          </div>
        </div>
      </div>

      <div className="fund-transfer-history">
        <h4>划转记录</h4>
        {transfers.length === 0 ? (
          <p className="account-overview-empty">暂无记录</p>
        ) : (
          <div className="fund-transfer-table-wrap">
            <table className="fund-transfer-table">
              <thead>
                <tr>
                  <th>时间</th>
                  <th>类型</th>
                  <th>路径</th>
                  <th>金额</th>
                  <th>状态</th>
                </tr>
              </thead>
              <tbody>
                {transfers.map((row) => (
                  <tr key={row.plan_id}>
                    <td>{formatTs(row.started_at)}</td>
                    <td>{row.trigger_type === 'auto' ? '自动' : '手动'}</td>
                    <td>
                      {row.source_exchange}
                      {row.source_exchange !== row.target_exchange ? ` → ${row.target_exchange}` : ' 所内'}
                    </td>
                    <td>{formatUsd(row.actual_amount ?? row.planned_amount)}</td>
                    <td>
                      <span className={`fund-transfer-status ${statusClass(row.status)}`}>
                        {statusLabel(row.status)}
                      </span>
                      {row.error_message ? (
                        <span className="fund-transfer-error" title={row.error_message}>
                          ⓘ
                        </span>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
